import { request } from '@akerolabs/akero-vue/src/utils/request'
import date from '@akerolabs/akero-vue/src/utils/date'

import {
  slugger,
  capitalizeFirstLetter,
} from '@akerolabs/akero-vue/src/utils/helpers'

/**
 * @param {Object}  options - The options for the validation rule
 * @returns {AsyncFunction}
 * @description A simple validation rule to check for uniqueness of slugs
 * on objects in the Akero API
 * @returns {AsyncFunction}
 */
export const uniqueValidtion = (options) => {
  if (!options.account || typeof options.account !== 'function') {
    throw new Error('Account property must be a function')
  }
  if (!options.agency || typeof options.agency !== 'function') {
    throw new Error('Agency property must be a function')
  }

  const returnType = (type) => {
    if (['app', 'embed', 'promo'].indexOf(type) !== -1) {
      return 'app'
    }
    return type
  }

  const errorMessage = () => {
    return typeof options.message === 'string'
      ? options.message
      : `${capitalizeFirstLetter(options.property)} must be unique`
  }

  const checkValidity = async (value) => {
    if (typeof value === 'undefined' || value === '') return true
    const params = {}
    const type = returnType(options.type)
    let property = value.trim()

    // If we have the current property we can use this to exclue the
    // current app
    if (options.current) {
      const current =
        typeof options.current === 'function'
          ? options.current()
          : options.current
      params._id = JSON.stringify({ $ne: current })
    }

    // we need to add the account and agency properties into the query
    // params so we are validating against the right account
    params.account = options.account()
    params.agency = options.agency()

    // Need to add some additional properties for validating apps
    if (options.type === 'promo') params.layoutBuilder = true
    if (options.type === 'embed') params.layoutBuilder = false

    // if the slugify option is passed in then we will convert the
    // required property to a akero slug
    if (options.slugify) property = slugger(property)
    params[options.property] = property

    const { data: res } = await request.get(`/validate/${type}`, {
      params,
    })

    return res.data.valid
  }

  return async (input) => {
    if (!(await checkValidity(input.value))) {
      input.setCustomValidity(errorMessage())
      return false
    } else {
      input.setCustomValidity('')
      return true
    }
  }
}

/**
 *
 * @param {Object} options - The options for the validation rule
 * @param {Function} options.foldersList - A function to return an array of used folders
 * @description A simple vaidation rule to check if a folder name has been used before.
 * @returns {AsyncFunction}
 */
export const uniqueFolderValidation = ({ foldersList }) => {
  if (typeof foldersList !== 'function') {
    throw new Error('foldersList must be a function')
  }

  return async (input) => {
    const existing = foldersList().map((i) => i.toLowerCase())
    if (existing.some((i) => i === input.value.toLowerCase())) {
      input.setCustomValidity('A unique folder name is required')
      return false
    } else {
      input.setCustomValidity('')
      return true
    }
  }
}

/**
 * @param {Object} param
 * @param {Function} param.startDate
 * @param {Function} param.endDate
 * @returns {Object}
 * @description Returns validation functions for the start and end datepickers,
 * you need to pass functions in for startDate and endDate which return the
 * values that we want to check, implementing this way as the date will already
 * be set once the datepicker changes, then the validations are run. makes it easier
 * to check each input from both without messing around.
 */
export const getStartEndValidation = ({ startDate, endDate }) => {
  const checkDates = (start, end) => {
    if (start.isBefore(end) || start.isSame(end)) {
      return true
    }
    return false
  }

  const startDateValidation = [
    async (input) => {
      const start = date(startDate()).startOf('day')
      const startPlusThree = date(start).add(3, 'months')
      const end = date(endDate()).endOf('day')

      if (endDate() && !checkDates(start, end)) {
        input.setCustomValidity('Start date cannot exceed end date.')
        return false
      } else if (endDate() && !checkDates(end, startPlusThree)) {
        input.setCustomValidity(
          'Start date cannot be more than 3 months before the end date.',
        )
      } else {
        input.setCustomValidity('')
        return true
      }
    },
  ]

  const endDateValidation = [
    async (input) => {
      const start = date(startDate()).startOf('day')
      const startPlusThree = date(start).add(3, 'months')
      const end = date(endDate()).endOf('day')

      if (startDate() && !checkDates(start, end)) {
        input.setCustomValidity('End date cannot be before start date.')
        return false
      } else if (startDate() && !checkDates(end, startPlusThree)) {
        input.setCustomValidity(
          'End date cannot be more than 3 months after the start date.',
        )
      } else {
        input.setCustomValidity('')
        return true
      }

      return true
    },
  ]

  return {
    startDateValidation,
    endDateValidation,
  }
}
